/**
 * Axios: при 401 пробуем обновить access_token через refresh_token,
 * иначе сбрасываем сессию. Без этого UI остаётся «залогиненным»,
 * а API после часа перестаёт отдавать данные.
 */
import axios from 'axios';

let interceptorsInstalled = false;
let isRefreshing = false;
let failedQueue = [];
let onSessionExpiredCb = () => {};

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

function isAuthRefreshUrl(url) {
  if (!url) return false;
  return url.includes('/auth/login') || url.includes('/auth/refresh');
}

/**
 * @param {{ onSessionExpired: () => void }} options
 */
export function setupAuthInterceptors({ onSessionExpired }) {
  onSessionExpiredCb = onSessionExpired || (() => {});

  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest || error.response?.status !== 401) {
        return Promise.reject(error);
      }
      if (originalRequest._retry || isAuthRefreshUrl(originalRequest.url)) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        onSessionExpiredCb();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          '/api/auth/refresh',
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } }
        );
        const newAccess = data.access_token;
        if (!newAccess) {
          throw new Error('Нет access_token в ответе refresh');
        }
        localStorage.setItem('token', newAccess);
        axios.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
        processQueue(null, newAccess);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        onSessionExpiredCb();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}
